import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Skeleton } from '@/components/ui/skeleton';
import { useSyncStatus, SYNC_STATUS_QUERY_KEY } from '../useSyncStatus';
import { SyncGlobalBar } from './SyncGlobalBar';

export const SyncStatusWidget = () => {
  const { data, isLoading, isSyncing, isComplete, overallProgress } = useSyncStatus();
  const queryClient = useQueryClient();
  const completionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (isComplete && !completionTimerRef.current) {
      completionTimerRef.current = setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: SYNC_STATUS_QUERY_KEY });
        completionTimerRef.current = null;
      }, 2000);
    }
    return () => {
      if (completionTimerRef.current) {
        clearTimeout(completionTimerRef.current);
        completionTimerRef.current = null;
      }
    };
  }, [isComplete, queryClient]);

  if (isLoading) {
    return <Skeleton className='h-14 w-full rounded-xl' />;
  }

  if (!isSyncing || !data) {
    return null;
  }

  const overallStatus = (() => {
    if (isComplete) return 'completed';
    if (data.overall_progress > 0) return 'running';
    return 'pending';
  })() as 'pending' | 'running' | 'completed' | 'failed' | 'skipped';

  const pendingExtractions = [
    ...(data.waves?.wave_1?.extractions ?? []),
    ...(data.waves?.wave_2?.extractions ?? []),
    ...(data.waves?.wave_3?.extractions ?? []),
  ].filter(e => e.status === 'running' || e.status === 'pending');

  return (
    <div className='w-full rounded-xl bg-h-primary-90 p-3 md:p-4'>
      <SyncGlobalBar
        progress={overallProgress}
        isComplete={isComplete}
        status={overallStatus}
        waveStatuses={{
          w1: data.waves?.wave_1?.status,
          w2: data.waves?.wave_2?.status,
          w3: data.waves?.wave_3?.status,
        }}
        pendingExtractions={pendingExtractions}
        trackClassName='bg-white/30'
      />
    </div>
  );
};
